import posthog from "posthog-js";

import { Logger } from "@/utils/logger";

const log = Logger.scope("Analytics");

/**
 * Shared PostHog client wrapper.
 *
 * Design goals (see task T1 / APPENDIX §B, §C):
 * - No-op cleanly when no key is configured (local dev / tests / CI). Every
 *   public helper guards on `enabled`, so calling code never has to check.
 * - Fire `$pageview` manually per SPA navigation (init uses `capture_pageview: false`).
 * - Only build person profiles for identified users (`person_profiles: "identified_only"`).
 * - Respect consent: default opted-out until consent is granted. Until T7's
 *   consent state lands we gate on `hasConsent()`, which currently returns true.
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

/** Extract a loggable message from an unknown thrown value. */
function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** True once a valid key is present and init has run. */
let enabled = false;

/**
 * Consent gate — placeholder until T7 wires the real consent banner state.
 * For now we allow tracking (non-EU / dev). T7 will replace the body of this
 * function; keep the signature stable so callers don't change.
 */
export function hasConsent(): boolean {
  return true;
}

export type IdentifyProps = {
  email?: string;
  /** "worker" | "employer"; omit when unknown rather than guessing (T3 is authoritative). */
  user_type?: "worker" | "employer";
  pm_user_id: string;
  /** ISO date string; omit when not known client-side. */
  signup_date?: string;
};

/**
 * Initialize PostHog once at app entry. Safe to call when no key is set — it
 * simply leaves analytics disabled and every other helper becomes a no-op.
 */
export function initAnalytics(): void {
  if (enabled) return;
  if (!POSTHOG_KEY) {
    log.info("PostHog key absent — analytics disabled (no-op)");
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // We fire $pageview manually on each TanStack Router navigation.
      capture_pageview: false,
      capture_pageleave: true,
      person_profiles: "identified_only",
      // Default opted-out; we opt in below only when consent is granted.
      opt_out_capturing_by_default: true,
      loaded: () => {
        log.info("PostHog loaded");
      },
    });
    enabled = true;

    if (hasConsent()) {
      optIn();
    }
  } catch (err) {
    // Never let analytics break app boot.
    log.error("PostHog init failed", {
      error: errMessage(err),
    });
    enabled = false;
  }
}

/** Grant consent and start capturing. No-op when analytics is disabled. */
export function optIn(): void {
  if (!enabled) return;
  try {
    posthog.opt_in_capturing();
  } catch (err) {
    log.error("PostHog opt-in failed", {
      error: errMessage(err),
    });
  }
}

/** Withdraw consent and stop capturing. No-op when analytics is disabled. */
export function optOut(): void {
  if (!enabled) return;
  try {
    posthog.opt_out_capturing();
  } catch (err) {
    log.error("PostHog opt-out failed", {
      error: errMessage(err),
    });
  }
}

/** Fire a manual `$pageview` for the current URL. Call on each navigation. */
export function capturePageview(url?: string): void {
  if (!enabled || !hasConsent()) return;
  try {
    posthog.capture("$pageview", {
      $current_url: url ?? window.location.href,
    });
  } catch (err) {
    log.error("PostHog pageview failed", {
      error: errMessage(err),
    });
  }
}

/**
 * Generic event capture. Kept minimal here; the funnel step events land in T2
 * and build on this wrapper.
 */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!enabled || !hasConsent()) return;
  try {
    posthog.capture(event, properties);
  } catch (err) {
    log.error("PostHog capture failed", {
      event,
      error: errMessage(err),
    });
  }
}

/**
 * Associate the current session with an identified user and set person
 * properties (APPENDIX §C). No PII beyond email/name should be sent here.
 */
export function identifyUser({
  email,
  user_type,
  pm_user_id,
  signup_date,
}: IdentifyProps): void {
  if (!enabled || !hasConsent()) return;
  if (!pm_user_id) return;
  try {
    const props: Record<string, unknown> = { pm_user_id };
    if (email) props.email = email;
    if (user_type) props.user_type = user_type;
    if (signup_date) props.signup_date = signup_date;
    posthog.identify(pm_user_id, props);
  } catch (err) {
    log.error("PostHog identify failed", {
      error: errMessage(err),
    });
  }
}

/** Clear the identified user (call on logout). */
export function resetUser(): void {
  if (!enabled) return;
  try {
    posthog.reset();
  } catch (err) {
    log.error("PostHog reset failed", {
      error: errMessage(err),
    });
  }
}
